import { useState, useEffect } from "react";
import ContactModal from "./ContactModal";
import { isEmailFormatCorrect } from '../../utils/formUtils';
import FloatingLabelInput from '../common/inputs/FloatingLabelInput';
import FloatingLabelTextarea from '../common/inputs/FloatingLabelTextArea';
import useFetchGraphQL from "../../hooks/useFetchGraphQL";
import Captcha from "../common/inputs/Captcha";
import { dateInSpain } from "../../utils/timeUtils";
import { useTranslation } from 'react-i18next';

const ContactForm = ({ t }) => {
    const { i18n } = useTranslation();

    const textTitle=t("pages.about_us.submenus.contact.texts.text2", { returnObjects: true });
    const textEmail=t("pages.about_us.submenus.contact.texts.text3", { returnObjects: true });
    const textName=t("pages.about_us.submenus.contact.texts.text9", { returnObjects: true });
    const textSubject=t("pages.about_us.submenus.contact.texts.text4", { returnObjects: true });
    const textBody=t("pages.about_us.submenus.contact.texts.text5", { returnObjects: true });
    const textButton=t("pages.about_us.submenus.contact.texts.text6", { returnObjects: true });
    const textP=t("pages.about_us.submenus.contact.texts.text7", { returnObjects: true });
    const subjects=t("pages.about_us.submenus.contact.subjects", { returnObjects: true });

    
    const [subject, setSubject] = useState('');
    const [subjectId, setSubjectId] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [name, setName] = useState('');
    const [body, setBody] = useState('');
    const [isSuccess, setIsSuccess] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { createContact } = useFetchGraphQL();
    const [captchaSuccess, setCaptchaSuccess] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();

      setEmailError(false);
      if (!isEmailFormatCorrect(email)) {
        setEmailError(true);
        return;
    }

      setLoading(true);
      const isSuccess = await create();
      setIsSuccess(isSuccess);
      setLoading(false);
      setIsModalOpen(true);
    };

    const create = async () => {
      try {
        const response = await createContact({ email: email, name: name, body: body, subject: subject, subjectId: subjectId, language: i18n.language });
        if (response.status === 200) { return true; }
        else { return false; }
      } catch (error) {
        return false;
      }
    };

    const handleCloseModal = (isSent) => {
      if (isSent) {
        window.location.reload();
      } else {
        setIsModalOpen(false);
      }
    };

    const handleChangeSubject = (selectedId) => {
      const foundEntry = Object.entries(subjects).find(([key, value]) => value.id === selectedId);
      if (foundEntry) {
          const [key, value] = foundEntry;
          setSubject(value.subject);
          setBody(prepareBody (key, value));
          setSubjectId(value.id);
      }
  };

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const hashToOption = {
        week: { value: subjects["week"], id: "1" },
        month: { value: subjects["month"], id: "2" },
        office: { value: subjects["office"], id: "3" },
        voucher: { value: subjects["voucher"], id: "4" }
    };

    if (hashToOption[hash]) {
        const foundEntry = hashToOption[hash];
        setSubjectId(foundEntry.id);
        handleChangeSubject(foundEntry.id)
    } else {
        const defaultSubject = subjects["week"];
        setSubjectId(defaultSubject.id);
        handleChangeSubject(defaultSubject.id)
    }
  }, []);

  useEffect(() => {
    handleChangeSubject(subjectId);
  }, [t]);

    return (
      <>
        <form className="booking-form" onSubmit={handleSubmit}>
            <h2 className="booking-form-title">{textTitle}</h2>
            <div className="generic-separator" />
            <p className="booking-form-p">{textP}</p>
            <div className="input-container">

              <FloatingLabelInput id="email" label={textEmail} value={email} onChange={(e) => setEmail(e.target.value)} error={emailError} />
              <FloatingLabelInput id="name" label={textName} value={name} onChange={(e) => setName(e.target.value)} />

                <div className="input-wrapper">
                  <select
                      className="input-field"
                      value={subjectId}
                      onChange={(e) => handleChangeSubject(e.target.value)}
                  >
                      {Object.entries(subjects).map(([key, value]) => (
                          <option key={key} value={value.id}>
                              {value.subject}
                          </option>
                      ))}
                  </select>
                  <label className="input-label" htmlFor="subject">{textSubject}</label>
                </div>

                <FloatingLabelTextarea id="body" label={textBody} value={body} onChange={(e) => setBody(e.target.value)} />
                <Captcha setCaptchaSuccess={setCaptchaSuccess} marginTopLabel="7px" />
            </div>
            {loading && <div className="loading-spinner" style={{height: "40px"}}><span>{t("pages.about_us.submenus.contact.texts.text8")}</span></div>}
            {!loading && <button type="submit" className="booking-form-button" disabled={!(body !== '' && subject !== '' && email !== '' && name !== '') || !captchaSuccess}>{textButton}</button>}
        </form>
        {isModalOpen && <ContactModal 
          t={t} 
          contact={{email: email, name: name, subject: subject, body: body, isSent: isSuccess}}
          onClose={handleCloseModal}
        />}
      </>
    );
  };
  
  export default ContactForm;

  const getDateRange = (type) => {
    const today = dateInSpain();
    let startDate, endDate;
  
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + ((1 - today.getDay() + 7) % 7 || 7)); 
    startDate = nextMonday;

    if (type === "week") { // week
      const nextFriday = new Date(nextMonday);
      nextFriday.setDate(nextMonday.getDate() + 4);
      endDate = nextFriday;
  
    } else if (type === "month") { // month
      const endDateRaw = new Date(nextMonday);
      endDateRaw.setDate(nextMonday.getDate() + 30);
      endDate = endDateRaw;
    } else {
      endDate = "DD/MM/YYYY";
    }
    
    return {
      startDate: formatDate(startDate),
      endDate: endDate !== "DD/MM/YYYY" ? formatDate(endDate) : endDate
    };
  };

  const formatDate = (date) => {
    const locale = 'es-ES';
    return date.toLocaleDateString(locale);
  };

  const prepareBody = (key, value) => {
    const dateRange = getDateRange(key);
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;

    return value.model
        .replace('%startDate%', startDate)
        .replace('%endDate%', endDate);
  }